import { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';
import { FaBars, FaTimes, FaShoppingCart, FaUser, FaSearch } from 'react-icons/fa';
import SearchBar from './navigation/SearchBar';
import MegaMenu from './navigation/MegaMenu';
import Button from './ui/Button';

const Navbar = ({ toggleCart, cartItemCount = 0 }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = useCallback(() => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setMenuOpen(false);
    setShowMobileSearch(false);
    navigate('/');
  }, [navigate]);

  const checkAdminStatus = useCallback(async () => {
    if (!isLoggedIn) {
      setIsAdmin(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoggedIn(false);
        return;
      }

      const response = await fetch('/api/user/check-admin', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.isAdmin);
      } else if (response.status === 401) {
        // Token is invalid, log out user
        handleSignOut();
      }
    } catch (error) {
      // Error will be handled by the global error handler
      setIsAdmin(false);
    }
  }, [isLoggedIn, handleSignOut]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    setShowMobileSearch(false);
  }, [location.pathname]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
    setShowMobileSearch(false);
  }, []);

  const toggleMobileSearch = useCallback(() => {
    setShowMobileSearch(prev => !prev);
    setMenuOpen(false);
  }, []);

  const handleSignInClick = useCallback(() => {
    navigate('/auth?mode=login');
  }, [navigate]);

  const handleSearch = useCallback(
    (query, filters) => {
      if (!query?.trim()) {
        return;
      }

      const searchParams = new URLSearchParams({
        q: query.trim(),
        ...filters,
      });
      navigate(`/shop?${searchParams.toString()}`);
    },
    [navigate],
  );

  const handleCategoryClick = useCallback(
    category => {
      if (category) {
        navigate(`/shop?category=${category}`);
      }
    },
    [navigate],
  );

  return (
    <>
      {/* Main Navbar */}
      <nav className='enhanced-navbar'>
        <div className='navbar-container'>
          {/* Left Section - Brand */}
          <div className='navbar-brand'>
            <Link to='/' className='brand-link'>
              AmFashion
            </Link>
          </div>

          {/* Center Section - Navigation Only */}
          <div className='navbar-center'>
            {/* Desktop Navigation */}
            <div className='desktop-nav'>
              <Link to='/' className='nav-link'>
                Home
              </Link>
              <MegaMenu
                trigger='Shop'
                onCategoryClick={handleCategoryClick}
                className='nav-mega-menu'
              />
              <Link to='/customize-outfit' className='nav-link'>
                Customize
              </Link>
              <Link to='/coming-soon' className='nav-link'>
                Coming Soon
              </Link>
              {isAdmin && (
                <Link to='/admin' className='nav-link admin-link'>
                  Admin
                </Link>
              )}
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className='navbar-actions'>
            {/* Mobile Search Toggle */}
            <button
              className='mobile-search-toggle'
              onClick={toggleMobileSearch}
              aria-label='Toggle search'
            >
              <FaSearch />
            </button>

            {/* User Account */}
            <div className='user-menu'>
              {isLoggedIn ? (
                <div className='user-dropdown'>
                  <button
                    className='user-button'
                    aria-label='User account menu'
                    aria-expanded='false'
                    aria-haspopup='true'
                  >
                    <FaUser aria-hidden='true' />
                    <span className='user-text'>Account</span>
                  </button>
                  <div className='user-dropdown-content' role='menu'>
                    <Link to='/dashboard' className='dropdown-item' role='menuitem'>
                      Dashboard
                    </Link>
                    <Link to='/profile' className='dropdown-item' role='menuitem'>
                      Profile
                    </Link>
                    <Link to='/orders' className='dropdown-item' role='menuitem'>
                      Orders
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className='dropdown-item sign-out'
                      role='menuitem'
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSignInClick}
                  className='sign-in-button'
                  aria-label='Sign in to your account'
                >
                  <FaUser aria-hidden='true' />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Shopping Cart */}
            <button
              className='cart-button'
              onClick={toggleCart}
              aria-label={`Shopping cart with ${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}`}
            >
              <div className='cart-icon-wrapper'>
                <FaShoppingCart aria-hidden='true' />
                {cartItemCount > 0 && (
                  <span className='cart-badge' aria-label={`${cartItemCount} items in cart`}>
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </div>
              <span className='cart-text'>Cart</span>
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className='mobile-menu-toggle'
              onClick={toggleMenu}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls='mobile-nav-menu'
            >
              {menuOpen ? <FaTimes aria-hidden='true' /> : <FaBars aria-hidden='true' />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className={`mobile-search-container ${showMobileSearch ? 'show' : ''}`}>
          <SearchBar
            onSearch={handleSearch}
            placeholder='Search products, brands, styles...'
            className='mobile-search'
            showFilters
          />
        </div>

        {/* Mobile Navigation Menu */}
        <div
          id='mobile-nav-menu'
          className={`mobile-nav-menu ${menuOpen ? 'show' : ''}`}
          role='navigation'
          aria-label='Mobile navigation'
        >
          <div className='mobile-nav-content'>
            <Link to='/' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Home
            </Link>
            <Link to='/shop' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Shop
            </Link>

            {/* Mobile Categories */}
            <div className='mobile-categories'>
              <div className='mobile-category-title'>Categories</div>
              <Link
                to='/shop?category=women'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Women
              </Link>
              <Link
                to='/shop?category=men'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Men
              </Link>
              <Link
                to='/shop?category=accessories'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Accessories
              </Link>
              <Link
                to='/shop?category=shoes'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Shoes
              </Link>
            </div>

            <Link
              to='/customize-outfit'
              className='mobile-nav-link'
              onClick={() => setMenuOpen(false)}
            >
              Customize
            </Link>
            <Link to='/coming-soon' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Coming Soon
            </Link>

            {isAdmin && (
              <Link
                to='/admin'
                className='mobile-nav-link admin-link'
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </Link>
            )}

            {/* Mobile User Actions */}
            <div className='mobile-user-actions'>
              {isLoggedIn ? (
                <>
                  <Link
                    to='/dashboard'
                    className='mobile-nav-link'
                    onClick={() => setMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to='/profile'
                    className='mobile-nav-link'
                    onClick={() => setMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link to='/orders' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
                    Orders
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className='mobile-nav-button'
                    aria-label='Sign out of your account'
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSignInClick}
                  className='mobile-nav-button primary'
                  aria-label='Sign in to your account'
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default memo(Navbar);
